import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Ticket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

const priorityBadge = { alta: 'badge-danger', media: 'badge-warning', baja: 'badge-info', urgente: 'badge-danger' };
const statusBadge = { abierto: 'badge-info', en_curso: 'badge-warning', esperando_cliente: 'badge-gray', resuelto: 'badge-success', cerrado: 'badge-gray' };
const statusLabels = { abierto: 'Abierto', en_curso: 'En curso', esperando_cliente: 'Esperando cliente', resuelto: 'Resuelto', cerrado: 'Cerrado' };

const Tickets = () => {
  const { hasPermission } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useUserPreference('tickets:search', '');
  const [statusFilter, setStatusFilter] = useUserPreference('tickets:statuses', []);
  const [viewMode, setViewMode] = useViewMode('tickets');
  const fl = useFieldLayout('tickets', viewMode);
  const navigate = useNavigate();
  const modal = useModal();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/tickets');
        setTickets(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        modal.error('Error cargando los tickets');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (date) => new Date(date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatShortDate = (date) => new Date(date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const daysUntil = (date) => {
    if (!date) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    return Math.round((target - now) / (1000 * 60 * 60 * 24));
  };

  const filtered = tickets.filter(t => {
    const matchSearch = t.code.toLowerCase().includes(search.toLowerCase()) ||
      t.subject.toLowerCase().includes(search.toLowerCase()) ||
      t.customer?.name?.toLowerCase().includes(search.toLowerCase()) ||
      (t.assignee?.name || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter.length === 0 || statusFilter.includes(t.status);
    return matchSearch && matchStatus;
  });

  const columns = [
    { key: 'code', header: 'Código', className: 'dt-nowrap', render: (t) => (
      <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.8rem' }}>{t.code}</span>
    ) },
    { key: 'priority', header: 'Prioridad', className: 'dt-nowrap', render: (t) => (
      <span className={`badge ${priorityBadge[t.priority] || 'badge-gray'}`}>{t.priority}</span>
    ) },
    { key: 'subject', header: 'Asunto', className: 'dt-strong', fixed: true, render: (t) => t.subject },
    { key: 'status', header: 'Estado', className: 'dt-nowrap', render: (t) => (
      <span className={`badge ${statusBadge[t.status] || 'badge-gray'}`}>
        {statusLabels[t.status] || t.status}
      </span>
    ) },
    { key: 'customer', header: 'Cliente', render: (t) => t.customer?.name },
    { key: 'assignee', header: 'Asignado', render: (t) => t.assignee?.name || '—' },
    { key: 'category', header: 'Categoría', render: (t) => t.category || '—' },
    { key: 'expectedCompletionDate', header: 'Fecha prevista', className: 'dt-nowrap', render: (t) => {
      if (!t.expectedCompletionDate) return '—';
      const days = daysUntil(t.expectedCompletionDate);
      return (
        <span style={{
          fontSize: '0.82rem',
          color: days !== null && days < 0 ? '#dc2626' : days !== null && days <= 2 ? '#b45309' : 'inherit',
          fontWeight: days !== null && days <= 2 ? 600 : 400
        }}>
          {formatShortDate(t.expectedCompletionDate)}
          {days !== null && (
            <span style={{ marginLeft: 6, fontSize: '0.75rem', color: '#64748b' }}>
              ({days < 0 ? `${Math.abs(days)} días de retraso` : days === 0 ? 'hoy' : `${days} días`})
            </span>
          )}
        </span>
      );
    } },
    { key: 'createdAt', header: 'Creado', className: 'dt-nowrap', render: (t) => (
      <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>{formatDate(t.createdAt)}</span>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Tickets</h1>
          <p className="page-subtitle">{tickets.length} tickets registrados</p>
        </div>
        {hasPermission('tickets', 'create') && (
          <button className="btn btn-primary" onClick={() => navigate('/tickets/new')}>
            <Plus size={18} /> Nuevo ticket
          </button>
        )}
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <Search />
            <input type="text" className="form-input" placeholder="Buscar por código, asunto, cliente o asignado..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 12, marginBottom: 4 }}>
          <span style={{ fontSize: '0.85rem', color: '#64748b', marginRight: 4 }}>Estado:</span>
          {Object.entries(statusLabels).map(([value, label]) => {
            const active = statusFilter.includes(value);
            return (
              <button
                key={value}
                type="button"
                className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setStatusFilter(active ? statusFilter.filter(s => s !== value) : [...statusFilter, value])}
              >
                {label}
              </button>
            );
          })}
          {statusFilter.length > 0 && (
            <button type="button" className="btn btn-sm btn-outline" onClick={() => setStatusFilter([])}>
              Limpiar
            </button>
          )}
          {statusFilter.length === 0 && (
            <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>(ninguno marcado = se muestran todos)</span>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Ticket size={64} />
            <h3>No hay tickets</h3>
            <p>Crea un ticket para empezar a gestionar incidencias</p>
          </div>
        ) : viewMode === 'table' ? (
          <DataTable
            columns={fl.orderColumns(columns)}
            rows={filtered}
            hiddenKeys={fl.hidden}
            getRowProps={(t) => ({ onClick: () => navigate(`/tickets/${t.id}`), style: { cursor: 'pointer' } })}
          />
        ) : (
          <div className="items-grid">
            {filtered.map(ticket => {
              const days = daysUntil(ticket.expectedCompletionDate);
              return (
                <div className="item-card" key={ticket.id} style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/tickets/${ticket.id}`)}>
                  <div className="item-card-header">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                        {!fl.isHidden('code') && <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.8rem' }}>{ticket.code}</span>}
                        {!fl.isHidden('priority') && <span className={`badge ${priorityBadge[ticket.priority] || 'badge-gray'}`}>{ticket.priority}</span>}
                      </div>
                      <div className="item-card-title">{ticket.subject}</div>
                    </div>
                    {!fl.isHidden('status') && (
                      <span className={`badge ${statusBadge[ticket.status] || 'badge-gray'}`}>
                        {statusLabels[ticket.status] || ticket.status}
                      </span>
                    )}
                  </div>
                  <div className="item-card-body">
                    {!fl.isHidden('customer') && (
                      <div className="item-card-row" style={fl.cardStyle('customer')}>
                        <span className="item-label">Cliente</span>
                        <span>{ticket.customer?.name}</span>
                      </div>
                    )}
                    {!fl.isHidden('assignee') && (
                      <div className="item-card-row" style={fl.cardStyle('assignee')}>
                        <span className="item-label">Asignado</span>
                        <span>{ticket.assignee?.name || '—'}</span>
                      </div>
                    )}
                    {!fl.isHidden('category') && (
                      <div className="item-card-row" style={fl.cardStyle('category')}>
                        <span className="item-label">Categoría</span>
                        <span>{ticket.category || '—'}</span>
                      </div>
                    )}
                    {!fl.isHidden('expectedCompletionDate') && ticket.expectedCompletionDate && (
                      <div className="item-card-row" style={fl.cardStyle('expectedCompletionDate')}>
                        <span className="item-label">Fecha prevista</span>
                        <span style={{
                          fontSize: '0.82rem',
                          color: days !== null && days < 0 ? '#dc2626' : days !== null && days <= 2 ? '#b45309' : 'inherit',
                          fontWeight: days !== null && days <= 2 ? 600 : 400
                        }}>
                          {formatShortDate(ticket.expectedCompletionDate)}
                          {days !== null && (
                            <span style={{ marginLeft: 6, fontSize: '0.75rem', color: '#64748b' }}>
                              ({days < 0 ? `${Math.abs(days)} días de retraso` : days === 0 ? 'hoy' : `${days} días`})
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {!fl.isHidden('createdAt') && (
                      <div className="item-card-row" style={fl.cardStyle('createdAt')}>
                        <span className="item-label">Creado</span>
                        <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>{formatDate(ticket.createdAt)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Tickets;
